// Records one spoken question with the microphone and stops by itself when
// you finish talking. Used where the browser has no speech recognition of
// its own (Android WebViews such as the Scalefusion browser): the WAV goes
// to the server, which transcribes it with Azure AI Speech.

export const hasMic = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

const RATE = 16_000;

/** Average-downsample mono float samples to 16 kHz. */
export function downsample(input: Float32Array, fromRate: number, toRate = RATE): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const out = new Float32Array(Math.floor(input.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < end; j++) sum += input[j];
    out[i] = sum / Math.max(1, end - start);
  }
  return out;
}

/** 16-bit PCM mono WAV. */
export function encodeWav(samples: Float32Array, rate = RATE): Blob {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buf);
  const str = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  str(0, 'RIFF');
  v.setUint32(4, 36 + samples.length * 2, true);
  str(8, 'WAVEfmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  str(36, 'data');
  v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([buf], { type: 'audio/wav' });
}

export interface Recording {
  /** Finish now and keep what was said. */
  stop: () => void;
  /** Throw it away. */
  abort: () => void;
  /** Resolves with the WAV, or undefined if nothing was said / aborted. */
  done: Promise<Blob | undefined>;
}

/**
 * Start recording. Ends after ~1.2 s of quiet following speech, after 15 s,
 * or after 7 s if nobody speaks. `onLevel` gets 0–1 for the orb animation.
 */
export async function recordUtterance(opts: { onLevel?: (level: number) => void; onSpeech?: () => void } = {}): Promise<Recording> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
  let ctx: AudioContext;
  let source: MediaStreamAudioSourceNode;
  let proc: ScriptProcessorNode;
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new Ctx();
    await ctx.resume().catch(() => undefined);
    source = ctx.createMediaStreamSource(stream);
    proc = ctx.createScriptProcessor(4096, 1, 1);
  } catch (err) {
    // Never leave the microphone on if setup fails.
    stream.getTracks().forEach((t) => t.stop());
    throw err;
  }
  const chunks: Float32Array[] = [];
  const started = performance.now();
  let noise = 0.01;
  let spoke = false;
  let lastLoud = 0;
  let finished = false;
  let keep = true;
  let resolveDone!: (b: Blob | undefined) => void;
  const done = new Promise<Blob | undefined>((r) => (resolveDone = r));

  // A wall-clock limit too: if audio processing stalls (a suspended context in
  // a WebView), the mic must still close.
  const hardStop = setTimeout(() => finish(spoke), 16_000);

  const finish = (save: boolean) => {
    if (finished) return;
    finished = true;
    clearTimeout(hardStop);
    keep = save;
    proc.disconnect();
    source.disconnect();
    stream.getTracks().forEach((t) => t.stop());
    void ctx.close().catch(() => undefined);
    if (!keep || !spoke || !chunks.length) return resolveDone(undefined);
    const total = chunks.reduce((n, c) => n + c.length, 0);
    const all = new Float32Array(total);
    let o = 0;
    for (const c of chunks) {
      all.set(c, o);
      o += c.length;
    }
    resolveDone(encodeWav(downsample(all, ctx.sampleRate)));
  };

  proc.onaudioprocess = (e) => {
    if (finished) return;
    const data = e.inputBuffer.getChannelData(0);
    chunks.push(new Float32Array(data));
    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
    const rms = Math.sqrt(sum / data.length);
    const now = performance.now();
    // Learn the room's background level in the first moment.
    // (Capped, so someone who starts talking straight away still registers as speech.)
    if (now - started < 350) noise = Math.min(0.03, Math.max(noise, rms));
    const loud = rms > Math.max(0.02, noise * 2.5);
    if (loud) {
      if (!spoke) opts.onSpeech?.();
      spoke = true;
      lastLoud = now;
    }
    opts.onLevel?.(Math.min(1, rms * 8));
    if (spoke && now - lastLoud > 1200) finish(true);
    else if (!spoke && now - started > 7000) finish(false);
    else if (now - started > 15_000) finish(true);
  };
  source.connect(proc);
  // ScriptProcessor only runs when connected to the output; it writes silence.
  proc.connect(ctx.destination);

  return { stop: () => finish(true), abort: () => finish(false), done };
}
